import React from "react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import {
	Card,
	CardContent,
	CardHeader,
	CardMessage,
	CardTitle
} from "src/common/components/Card";
import { DateInput } from "src/common/components/DateTime";
import { Field } from "src/common/components/Field";
import { GridColumn, GridRow } from "src/common/components/Grid";
import { FullCardPreloader } from "src/common/components/Preloader";
import { Tooltip } from "src/common/components/Tooltip";
import { useFormatName } from "src/common/hooks/useFormatName";
import { api } from "src/common/utils/api";
import { useKnownUsers } from "src/features/auth/hooks/useKnownUsers";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { genericMessages } from "src/messages/generic";

import "./ActivityChart.css";

interface UserActivityRecord {
	user: string;
	numberOfMeasurementsCreated: number;
}

const messages = defineMessages({
	userActivityTitle: {
		id: "stats.user_activity.title",
		defaultMessage: "Adatrögzítések"
	},
	userActivityHelp: {
		id: "stats.user_activity.help",
		defaultMessage:
			"A riport a kiválasztott időszakban a rendszerbe feltöltött összes adat százalékos eloszlását mutatja meg az adatrögzítést végző felhasználók között. Legfeljebb 60 nap hosszú időszak választható ki."
	},
	noData: {
		id: "stats.no_data",
		defaultMessage: "Nincs megjeleníthető adat"
	},
	dateRangeWarning: {
		id: "stats.date_range_warning",
		defaultMessage:
			"A megadott intervallum nem lehet hosszabb mint {days} nap. Az alábbi adatok a {from} - {to} intervallumnak felelnek meg."
	}
});

export const Stats: React.FunctionComponent = () => {
	return (
		<div className="stats">
			<GridRow>
				<GridColumn columnSize={6}>
					<ActivityChart />
				</GridColumn>
			</GridRow>
		</div>
	);
};

const activityChartLimitInDays = 60;

const initialDateTo = new Date();
initialDateTo.setHours(23);
initialDateTo.setMinutes(59);
initialDateTo.setSeconds(59);
initialDateTo.setMilliseconds(99);
const initialStrTo = dateToString(initialDateTo);

const initialDateFrom = new Date();
initialDateFrom.setHours(12);
initialDateFrom.setMinutes(0);
initialDateFrom.setSeconds(0);
initialDateFrom.setDate(initialDateFrom.getDate() - 30);
initialDateFrom.setHours(0);
const initialStrFrom = dateToString(initialDateFrom);

export const ActivityChart: React.FunctionComponent = () => {
	const { org, mod } = useOrgModParams();
	const users = useKnownUsers();

	const formatName = useFormatName();
	const createGenericError = useCreateGenericError();
	const { formatDate } = useIntl();

	const [strFrom, setStrFrom] = React.useState<string | undefined>(
		initialStrFrom
	);
	const [strTo, setStrTo] = React.useState<string | undefined>(initialStrTo);

	const minDateFrom = React.useMemo(() => {
		if (strTo === undefined) {
			return;
		}
		const d = new Date(strTo);
		d.setHours(12);
		d.setMinutes(0);
		d.setSeconds(0);
		d.setDate(d.getDate() - activityChartLimitInDays);
		d.setHours(0);
		return d;
	}, [strTo]);

	const dateFrom = React.useMemo(() => {
		if (strFrom === undefined) {
			return;
		}
		const d = new Date(strFrom);
		d.setHours(0);
		d.setMinutes(0);
		d.setSeconds(0);
		return d;
	}, [strFrom]);

	console.log(minDateFrom, dateFrom);

	const dateTo = React.useMemo(() => {
		if (strTo === undefined) {
			return;
		}
		const d = new Date(strTo);
		d.setHours(23);
		d.setMinutes(59);
		d.setSeconds(59);
		d.setMilliseconds(99);
		return d;
	}, [strTo]);

	const [isLoading, setLoading] = React.useState(false);
	const [data, setData] = React.useState<UserActivityRecord[]>([]);

	React.useEffect(() => {
		if (
			minDateFrom === undefined ||
			dateFrom === undefined ||
			dateTo === undefined
		) {
			setData([]);
		} else {
			const params = [`organization=${org}`, `module=${mod}`];
			if (dateFrom < minDateFrom) {
				params.push(`from=${minDateFrom.toISOString()}`);
			} else {
				params.push(`from=${dateFrom.toISOString()}`);
			}
			params.push(`to=${dateTo.toISOString()}`);
			const search = `${params.join("&")}`;
			(async () => {
				setLoading(true);
				try {
					const { data } = await api.get<UserActivityRecord[]>(
						`/stats/wa?${search}`
					);
					setLoading(false);
					setData(data);
				} catch {
					setLoading(false);
					createGenericError();
				}
			})();
		}
	}, [org, mod, minDateFrom, dateFrom, dateTo, createGenericError]);

	const rows = React.useMemo(() => {
		let sum = 0;
		for (const item of data) {
			sum += item.numberOfMeasurementsCreated;
		}
		const rows = data.map(({ user: id, numberOfMeasurementsCreated }) => {
			return {
				name: id in users ? formatName(users[id]) : id,
				value: numberOfMeasurementsCreated / (sum / 100)
			};
		});
		rows.sort((a, b) => {
			if (a.value < b.value) {
				return 1;
			}
			if (a.value > b.value) {
				return -1;
			}
			return 0;
		});
		return rows;
	}, [formatName, users, data]);

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<FormattedMessage {...messages.userActivityTitle} />
					<Tooltip
						placement="right"
						canWrap={true}
						maxWidth={300}
						topOffset={-17}
						leftOffset={1}
						title={
							<FormattedMessage {...messages.userActivityHelp} />
						}
						content={<FaRegQuestionCircle />}
					/>
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="activity-chart-filter">
					<GridRow>
						<GridColumn columnSize={6}>
							<Field
								label={
									<FormattedMessage
										{...genericMessages.dateFrom}
									/>
								}
							>
								<DateInput
									value={strFrom}
									onChangeValue={setStrFrom}
								/>
							</Field>
						</GridColumn>
						<GridColumn columnSize={6}>
							<Field
								label={
									<FormattedMessage
										{...genericMessages.dateTo}
									/>
								}
							>
								<DateInput
									value={strTo}
									onChangeValue={setStrTo}
								/>
							</Field>
						</GridColumn>
					</GridRow>
					{dateFrom !== undefined &&
						minDateFrom !== undefined &&
						dateTo !== undefined &&
						dateFrom < minDateFrom && (
							<div className="stats__warning">
								<FormattedMessage
									{...messages.dateRangeWarning}
									values={{
										days: activityChartLimitInDays,
										from: formatDate(minDateFrom, {
											year: "numeric",
											month: "numeric",
											day: "numeric"
										}),
										to: formatDate(dateTo, {
											year: "numeric",
											month: "numeric",
											day: "numeric"
										})
									}}
								/>
							</div>
						)}
				</div>
			</CardContent>
			<CardContent>
				{isLoading ? (
					<FullCardPreloader />
				) : rows.length === 0 ? (
					<CardMessage>
						<FormattedMessage {...messages.noData} />
					</CardMessage>
				) : (
					<div className="activity-chart">
						<div className="activity-chart__label-list">
							{rows.map((row, i) => (
								<div className="activity-chart__label" key={i}>
									{row.name}
								</div>
							))}
						</div>
						<div className="activity-chart__value-list">
							{rows.map((row, i) => (
								<div
									className="activity-chart__value-container"
									key={i}
								>
									<div
										className="activity-chart__bar"
										style={{ width: `${row.value}%` }}
									/>
									<div className="activity-chart__value">
										{row.value.toFixed(2)}%
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

function dateToString(d: Date): string {
	const s1 = `${d.getFullYear()}`;
	const s2 = `${d.getMonth() + 1}`.padStart(2, "0");
	const s3 = `${d.getDate()}`.padStart(2, "0");
	return `${s1}-${s2}-${s3}`;
}
